import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";
import { Calendar, Clapperboard, Clock, ExternalLink, Film, Globe, Play, Star, Ticket, Tv, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { WatchProviders } from "./WatchProviders";
import { CastCrew } from "./CastCrew";
import { UserReviews } from "./UserReviews";
import { SimilarMovies } from "./SimilarMovies";
import { WatchStatusSelector } from "./WatchStatusSelector";

interface MovieDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  movieId: number;
  mediaType: "movie" | "tv";
  isInWatchlist?: boolean;
  onToggleWatchlist?: (movieId: number, mediaType: "movie" | "tv", title?: string, posterPath?: string, year?: number | null) => void;
  userId?: string;
  isPremium?: boolean;
  pickReason?: string;
}

interface CrewScore {
  name: string;
  avg_rating: number;
  film_count: number;
}

interface MovieDetails {
  id: number;
  title: string;
  overview: string;
  poster: string | null;
  backdrop: string | null;
  rating: number;
  vote_count: number;
  release_date: string;
  runtime?: number;
  genres: string[];
  language: string;
  status: string;
  tagline?: string;
  homepage?: string;
  trailer?: string;
  trailer_key?: string;
  streaming_services: string[];
  cast: Array<{ id: number; name: string; character: string; profile_path: string | null }>;
  director?: string;
  writers: string[];
  media_type: "movie" | "tv";
  crew_scores?: { actors: CrewScore[]; director: CrewScore | null; producers: CrewScore[] };
  seasons?: number;
  episodes?: number;
  episode_runtime?: number[];
  networks?: string[];
  season_details?: Array<{
    season_number: number;
    name: string;
    episode_count: number;
    episodes: Array<{
      episode_number: number;
      name: string;
      rating: number | null;
      vote_count: number;
      air_date: string;
      overview: string;
    }>;
  }>;
}

export const MovieDetailModal = ({
  isOpen,
  onClose,
  movieId,
  mediaType,
  userId,
  isPremium = false,
  pickReason,
}: MovieDetailModalProps) => {
  const [details, setDetails] = useState<MovieDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [trailerLoading, setTrailerLoading] = useState(false);
  const [showEmbeddedTrailer, setShowEmbeddedTrailer] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && movieId) fetchDetails();
  }, [isOpen, movieId, mediaType, userId]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("tmdb-details", {
        body: { tmdbId: movieId, mediaType, includeGenreEstimate: true },
      });

      if (error) throw error;

      setDetails(data);
      // View intent is only recorded for ticket holders — guests leave no trace.
      if (userId) {
        supabase
          .from("viewing_history")
          .insert({ user_id: userId, tmdb_id: movieId, media_type: mediaType, watched_at: new Date().toISOString(), watch_count: 1 })
          .then(({ error: historyError }) => {
            if (historyError) console.error("Error tracking viewing history:", historyError);
          });
      }

    } catch (error) {
      console.error("Error fetching movie details:", error);
      toast({ title: "Error", description: "Failed to load movie details", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleWatchTrailer = () => {
    if (!details?.trailer) return;
    if (isPremium) {
      setShowEmbeddedTrailer(true);
    } else {
      setTrailerLoading(true);
      window.open(details.trailer, "_blank");
      setTimeout(() => setTrailerLoading(false), 1000);
    }

    if (userId) {
      supabase.from("usage_tracking").upsert({
        user_id: userId,
        feature_type: "trailer_view",
        usage_date: new Date().toISOString().split("T")[0],
        usage_count: 1,
      });
    }
  };

  const formatRuntime = (minutes?: number) => {
    if (!minutes) return "N/A";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };

  const releaseYear = (dateString?: string) => {
    if (!dateString) return "—";
    const year = new Date(dateString).getFullYear();
    return Number.isNaN(year) ? "—" : String(year);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-tutorial="movie-detail-modal" className="screening-room-modal w-[96vw] sm:w-[94vw] sm:max-w-6xl h-[92vh] p-0 flex flex-col overflow-hidden">
        <div className="screening-room-modal__glow" />
        <div className="screening-room-modal__marquee" aria-hidden="true">
          {Array.from({ length: 42 }).map((_, i) => <span key={i} style={{ animationDelay: `${i * 70}ms` }} />)}
        </div>
        <Button data-tutorial="movie-detail-close" variant="ghost" size="icon" onClick={onClose} className="screening-room-modal__close" aria-label="Close dialog">
          <X className="h-4 w-4" />
        </Button>

        {loading ? (
          <div className="screening-room-loading">
            <DialogHeader className="sr-only">
              <DialogTitle>Loading details</DialogTitle>
              <DialogDescription>Fetching title and information</DialogDescription>
            </DialogHeader>
            <div className="screening-room-loading__reel"><Clapperboard className="h-7 w-7" /></div>
            <p>Preparing the screening room</p>
          </div>
        ) : details ? (
          <div className="screening-room-scroll">
            <section className="screening-room-hero">
              {details.backdrop && <img src={details.backdrop} alt="" className="screening-room-hero__backdrop" aria-hidden="true" />}
              <div className="screening-room-hero__beam" aria-hidden="true" />
              <div className="screening-room-hero__content">
                <DialogHeader className="contents">
                  <div className="screening-room-poster-wrap">
                    {details.poster ? (
                      <img src={details.poster} alt={details.title} className="screening-room-poster" />
                    ) : (
                      <div className="screening-room-poster screening-room-poster--empty"><Film className="h-10 w-10" /></div>
                    )}
                  </div>

                  <div className="screening-room-billboard">
                    <div className="screening-room-kicker"><Ticket className="h-3.5 w-3.5" />{mediaType === "tv" ? "Series screening" : "Now showing"}</div>
                    <DialogTitle className="screening-room-title">{details.title}</DialogTitle>
                    {details.tagline ? (
                      <DialogDescription className="screening-room-tagline">“{details.tagline}”</DialogDescription>
                    ) : (
                      <DialogDescription className="sr-only">Movie details for {details.title}</DialogDescription>
                    )}

                    <div className="screening-room-stats">
                      <span><Star className="h-4 w-4 fill-current" />{details.rating}/10</span>
                      <span><Calendar className="h-4 w-4" />{releaseYear(details.release_date)}</span>
                      {details.runtime && <span><Clock className="h-4 w-4" />{formatRuntime(details.runtime)}</span>}
                      <span><Globe className="h-4 w-4" />{details.language.toUpperCase()}</span>
                    </div>

                    <div className="screening-room-genres">
                      {details.genres.map((genre, index) => <Badge key={`${genre}-${index}`} variant="outline">{genre}</Badge>)}
                    </div>

                    <div className="screening-room-actions" data-tutorial="watch-status">
                      <WatchStatusSelector
                        tmdbId={movieId}
                        mediaType={mediaType}
                        userId={userId}
                        title={details.title}
                        posterPath={details.poster}
                        releaseDate={details.release_date ? releaseYear(details.release_date) : null}
                        display="segmented"
                        allowRemove
                      />
                      {details.trailer && (
                        <Button size="sm" onClick={handleWatchTrailer} className="screening-room-trailer-btn" disabled={trailerLoading}>
                          <Play className="mr-1.5 h-3.5 w-3.5 flex-shrink-0" />
                          <span>{trailerLoading ? "Opening..." : isPremium ? "Play trailer" : "Open trailer"}</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </DialogHeader>
              </div>
            </section>

            <div className="screening-room-body">
              <main className="screening-room-main">
                {details.streaming_services.length > 0 && (
                  <section className="screening-room-ticket-strip">
                    <span className="screening-room-ticket-strip__label">Streaming</span>
                    <div className="flex flex-wrap gap-2">
                      {details.streaming_services.map((service, index) => <Badge key={`${service}-${index}`} variant="outline">{service}</Badge>)}
                    </div>
                  </section>
                )}

                {showEmbeddedTrailer && isPremium && details.trailer_key && (
                  <section
                    ref={(el) => {
                      if (el && !el.dataset.scrolled) {
                        el.dataset.scrolled = "1";
                        requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "center" }));
                      }
                    }}
                    className="screening-room-panel animate-in fade-in duration-500 scroll-mt-4"
                  >
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <h3 className="screening-room-panel-title mb-0">Trailer</h3>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" asChild className="h-7 text-xs">
                          <a href={`https://www.youtube.com/watch?v=${details.trailer_key}`} target="_blank" rel="noopener noreferrer">Open in YouTube</a>
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setShowEmbeddedTrailer(false)} className="h-7 text-xs">Close</Button>
                      </div>
                    </div>
                    <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                      <iframe
                        className="absolute top-0 left-0 w-full h-full rounded-md"
                        src={`https://www.youtube.com/embed/${details.trailer_key}?autoplay=1&playsinline=1&rel=0&modestbranding=1`}
                        title="Movie Trailer"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </section>
                )}

                <section className="screening-room-panel animate-in fade-in duration-500">
                  <h3 className="screening-room-panel-title">Programme notes</h3>
                  {pickReason && (
                    <p className="screening-room-pick-note">
                      <span className="screening-room-pick-note-label">Tonight's pick</span>
                      <span>{pickReason}</span>
                    </p>
                  )}
                  <p className="screening-room-synopsis">{details.overview || "No overview available."}</p>
                </section>

                <Tabs defaultValue="cast" className="w-full screening-room-panel screening-room-tabs">
                  <TabsList data-tutorial="movie-detail-tabs" className={`screening-room-tabs-list grid w-full ${mediaType === "tv" && details.season_details?.length ? "grid-cols-4" : "grid-cols-3"}`}>
                    <TabsTrigger data-tutorial="movie-detail-tab-cast" value="cast" className="screening-room-tab-trigger"><span className="sm:hidden">Cast</span><span className="hidden sm:inline">Cast & Crew</span></TabsTrigger>
                    <TabsTrigger data-tutorial="movie-detail-tab-watch" value="watch" className="screening-room-tab-trigger"><span className="sm:hidden">Watch</span><span className="hidden sm:inline">Watch Options</span></TabsTrigger>
                    <TabsTrigger data-tutorial="movie-detail-tab-reviews" value="reviews" className="screening-room-tab-trigger">Reviews</TabsTrigger>
                    {mediaType === "tv" && details.season_details?.length > 0 && <TabsTrigger data-tutorial="movie-detail-tab-episodes" value="episodes" className="screening-room-tab-trigger">Episodes</TabsTrigger>}
                  </TabsList>

                  <TabsContent value="cast" className="space-y-4 mt-4"><CastCrew tmdbId={movieId} mediaType={mediaType} crewScores={details.crew_scores} /></TabsContent>
                  <TabsContent value="watch" className="space-y-4 mt-4"><WatchProviders tmdbId={movieId} mediaType={mediaType} userId={userId} /></TabsContent>
                  <TabsContent value="reviews" className="space-y-4 mt-4"><UserReviews tmdbId={movieId} mediaType={mediaType} title={details.title} /></TabsContent>

                  {mediaType === "tv" && details.season_details?.length > 0 && (
                    <TabsContent value="episodes" className="space-y-4 mt-4">
                      <h3 className="screening-room-panel-title">Episode ledger</h3>
                      <Accordion type="single" collapsible className="w-full space-y-2">
                        {details.season_details.map((season) => (
                          <AccordionItem key={season.season_number} value={`season-${season.season_number}`} className="screening-room-episode-season">
                            <AccordionTrigger className="hover:no-underline">
                              <div className="flex items-center justify-between w-full pr-4">
                                <span className="font-medium">{season.name}</span>
                                <span className="text-sm text-muted-foreground">{season.episode_count} episodes</span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="space-y-2 pt-2">
                                {season.episodes.map((episode) => {
                                  const rating = episode.rating || 0;
                                  const ratingColor = rating >= 8 ? "text-success" : rating >= 7 ? "text-warning" : rating >= 6 ? "text-accent" : "text-muted-foreground";
                                  const starFill = rating >= 8 ? "fill-success" : rating >= 7 ? "fill-warning" : rating >= 6 ? "fill-accent" : "";
                                  return (
                                    <div key={episode.episode_number} className="screening-room-episode">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span className="font-medium text-sm">{episode.episode_number}. {episode.name}</span>
                                          {episode.air_date && <span className="text-xs text-muted-foreground">{new Date(episode.air_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>}
                                        </div>
                                        {episode.overview && <p className="text-xs text-muted-foreground line-clamp-2">{episode.overview}</p>}
                                      </div>
                                      <div className="flex items-center gap-1 ml-3 flex-shrink-0">
                                        <Star className={`h-4 w-4 ${ratingColor} ${starFill}`} />
                                        <span className={`text-sm font-semibold min-w-[2.5rem] text-right ${ratingColor}`}>{episode.rating ? episode.rating.toFixed(1) : "N/A"}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </TabsContent>
                  )}
                </Tabs>

                <section className="screening-room-panel screening-room-similar">
                  <h3 className="screening-room-panel-title">Encore picks</h3>
                  <SimilarMovies movieId={movieId} mediaType={mediaType} userId={userId} isPremium={isPremium} />
                </section>
              </main>

              <aside className="screening-room-sidecar">
                <section className="screening-room-pass">
                  <div className="screening-room-pass__edge" aria-hidden="true" />
                  <p className="screening-room-pass__label">Admit one</p>
                  <div className="screening-room-pass__icon">{mediaType === "tv" ? <Tv className="h-5 w-5" /> : <Film className="h-5 w-5" />}</div>
                  <p className="screening-room-pass__title">{details.title}</p>
                  <div className="screening-room-pass__barcode" aria-hidden="true">{Array.from({ length: 18 }).map((_, i) => <span key={i} style={{ height: `${35 + ((i * 17) % 60)}%` }} />)}</div>
                </section>

                {mediaType === "tv" && (details.seasons || details.episodes) && (
                  <section className="screening-room-panel">
                    <h3 className="screening-room-panel-title">Series file</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {details.seasons && <div className="screening-room-stat-card"><p>Seasons</p><strong>{details.seasons}</strong></div>}
                      {details.episodes && <div className="screening-room-stat-card"><p>Episodes</p><strong>{details.episodes}</strong></div>}
                      {details.episode_runtime?.[0] && <div className="screening-room-stat-card col-span-2"><p>Episode Runtime</p><strong>{formatRuntime(details.episode_runtime[0])}</strong></div>}
                    </div>
                    {details.networks && details.networks.length > 0 && (
                      <div className="mt-4">
                        <p className="screening-room-small-label">Networks</p>
                        <div className="flex flex-wrap gap-2">{details.networks.map((network) => <Badge key={network} variant="outline">{network}</Badge>)}</div>
                      </div>
                    )}
                  </section>
                )}

                <section className="screening-room-panel">
                  <h3 className="screening-room-panel-title">Release card</h3>
                  <dl className="screening-room-release-list">
                    <div><dt>Status</dt><dd>{details.status}</dd></div>
                    <div><dt>Release</dt><dd>{formatDate(details.release_date)}</dd></div>
                    <div><dt>Votes</dt><dd>{details.vote_count.toLocaleString()}</dd></div>
                  </dl>
                  {details.homepage && (
                    <a href={details.homepage} target="_blank" rel="noopener noreferrer" className="screening-room-homepage">
                      Official site <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </section>
              </aside>
            </div>
          </div>
        ) : (
          <div className="screening-room-loading">
            <DialogHeader className="sr-only">
              <DialogTitle>Details unavailable</DialogTitle>
              <DialogDescription>Failed to load details</DialogDescription>
            </DialogHeader>
            <p>Failed to load details</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};